import {
  Box,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useRef, useState } from 'react';

import ProgressBar from '@/components/shared/ProgressBar';
import { SearchResultsList } from '@/components/shared/SearchBox';
import { useSearchBox } from '@/components/shared/SearchBox/useSearchBox';
import SearchContainer from './SearchContainer';
import SearchIconButton from './SearchIconButton';
import SearchTextField from './SearchTextField';
import type { RSSearchBoxProps } from './types';

const RSSearchBoxMobile: React.FC<RSSearchBoxProps> = ({
  sx,
  placeholder,
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  searchResults,
  isLoading = false,
  onNeedHelpClick,
  onContinueClick,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    displayValue,
    selectedResult,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
  } = useSearchBox({
    value,
    selectedResult: controlledSelected,
    onSelect,
    onChange,
    searchResults,
  });

  return (
    <>
      <SearchContainer sx={sx} data-testid='rs-search-box-mobile-trigger'>
        <Stack
          direction='row'
          spacing={2}
          onClick={() => setOpen(true)}
          aria-haspopup='dialog'
          aria-expanded={open ? 'true' : 'false'}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 0.5,
            pl: 1,
          }}
          data-testid='rs-search-box-mobile-trigger-content'
        >
          <Typography
            sx={{
              textAlign: 'left',
              color: 'grey.500',
            }}
          >
            {placeholder}
          </Typography>
          <SearchIconButton data-testid='rs-search-box-mobile-trigger-search-button' />
        </Stack>
      </SearchContainer>

      <Modal
        open={open}
        variants='bottom-sheet'
        sx={{}}
        slotProps={{
          paper: {
            sx: {
              height: '90%',
              pb: 0,
            },
          },
        }}
        data-testid='rs-search-box-mobile-modal'
      >
        <Stack
          direction='column'
          spacing={1}
          sx={{
            height: '100%',
          }}
        >
          <Stack
            direction='row'
            spacing={2}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='b1Bold'>Search destinations</Typography>
            <IconButton
              iconName='xmark-regular'
              onClick={() => setOpen(false)}
              data-testid='rs-search-box-mobile-close-button'
            />
          </Stack>
          <SearchContainer
            ref={containerRef}
            sx={sx}
            data-testid='rs-search-box-mobile-search-container'
          >
            <Stack
              direction='row'
              spacing={1}
              aria-haspopup='dialog'
              aria-expanded={open ? 'true' : 'false'}
              sx={(theme) => ({
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 0.5,
                '&:hover': {
                  backgroundColor: 'primary.100',
                  '& .MuiInputBase-root': {
                    backgroundColor: `${theme.palette.primary[100]} !important`,
                  },
                },
              })}
            >
              <SearchTextField
                placeholder={placeholder}
                value={displayValue}
                onChange={handleSearchChange}
                selectedResult={selectedResult}
                onClearSelected={handleClearSelected}
              />
              <SearchIconButton
                loading={isLoading}
                variant={selectedResult ? 'continue' : 'search'}
                onClick={onContinueClick}
                data-testid='rs-search-box-mobile-modal-search-button'
              />
            </Stack>
          </SearchContainer>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <SearchResultsList
              onSelect={handleSelect}
              open={
                (value !== '' && !!searchResults) || !!searchResults?.length
              }
              results={searchResults}
              footer={{
                label: `Destination not listed`,
                onClick: onNeedHelpClick,
              }}
            />
          </Box>
          <ProgressBar
            displayBackButton={false}
            buttonDisabled={!selectedResult}
            onClickContinue={onContinueClick}
          />
        </Stack>
      </Modal>
    </>
  );
};

export default RSSearchBoxMobile;
